import { useState } from "react";
import {
  Globe, Database, Shield, ChevronDown, ChevronUp,
  Network, Building2, Landmark, FileText,
  Clock, Zap
} from "lucide-react";
import {
  OBJECTIONS, HACKATHON_PROPOSAL, KOBZAR_QUOTES, NEWS_CITATIONS
} from "../../data/presentationData";
import { SW, KobzarQuote, CitationBar, PdfPreviewModal } from "./shared";

export function Section9() {
  const [expO, setExpO] = useState(null);
  const [showHackathon, setShowHackathon] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const icons = { dollar: Landmark, shield: Shield, scale: Building2, network: Network, flag: Globe, database: Database, clock: Clock };
  return (
    <SW id="section-9">
      <div className="mb-10 text-center stagger-1">
        <span className="text-[#005BBB] text-xs font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'var(--font-body)' }}>The Ask</span>
        <h2 className="text-4xl md:text-5xl font-bold mt-3 text-slate-900">One Course. One Semester. First Mover.</h2>
      </div>
      <div className="max-w-2xl mx-auto mb-10 stagger-2">
        <div className="bg-[#1B365D] rounded-2xl p-8 text-white">
          {[{ label: "Format", value: "Practical Data Science course, Fall 2026" },
            { label: "Students", value: "12\u201314 students in small groups, 3\u20134 projects from 16 proposed" },
            { label: "In-Country", value: "KSE (Kyiv School of Economics) or KPI (Igor Sikorsky Polytechnic)" },
            { label: "Data Access", value: "MDT open data portal (data.gov.ua) + ProZorro API + targeted partnerships" },
            { label: "Warm Paths", value: "FSU (4 hrs, 3 MOUs) + UCL MDT partnership + USAID DTA ($150M program)" },
            { label: "Precedent", value: "NCF-USDA model \u2014 government provides data, students deliver analysis, PI supervises" },
            { label: "Deliverable", value: "Published student analyses NCF can showcase nationally and internationally" },
          ].map((r, i) => (
            <div key={i} className={`flex gap-4 py-3.5 ${i > 0 ? "border-t border-white/10" : ""}`}>
              <span className="text-[#C5A258] font-semibold text-sm w-28 flex-shrink-0" style={{ fontFamily: 'var(--font-body)' }}>{r.label}</span>
              <span className="text-slate-200 text-sm" style={{ fontFamily: 'var(--font-body)' }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Hackathon Proposal */}
      <div className="mb-10 stagger-3">
        <div className="bg-[#005BBB]/5 border-2 border-[#005BBB]/20 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <Zap className="w-6 h-6 text-[#005BBB] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 text-lg mb-1" data-testid="hackathon-title">{HACKATHON_PROPOSAL.name}</h3>
              <p className="text-xs text-[#005BBB] font-semibold mb-3" style={{ fontFamily: 'var(--font-body)' }}>{HACKATHON_PROPOSAL.format}</p>
              <p className="text-sm text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'var(--font-body)' }}>{HACKATHON_PROPOSAL.description}</p>
              <button data-testid="toggle-hackathon-details" onClick={() => setShowHackathon(!showHackathon)}
                className="text-sm text-[#005BBB] hover:text-blue-800 flex items-center gap-1 font-medium transition" style={{ fontFamily: 'var(--font-body)' }}>
                {showHackathon ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showHackathon ? "Hide" : "Show"} Hackathon Details
              </button>
              {showHackathon && (
                <div className="mt-4 space-y-4 tooltip-enter">
                  <div>
                    <h4 className="font-semibold text-sm text-slate-800 mb-2">Challenge Tracks</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {HACKATHON_PROPOSAL.tracks.map((t, i) => (
                        <div key={i} className="bg-white border border-slate-200 rounded-lg p-3">
                          <div className="font-bold text-xs text-[#005BBB]">{t.name}</div>
                          <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: 'var(--font-body)' }}>{t.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-slate-800 mb-2">Why FSU</h4>
                    <p className="text-xs text-slate-700 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{HACKATHON_PROPOSAL.whyFSU}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-slate-800 mb-2">Expected Outcomes</h4>
                    <div className="flex flex-wrap gap-2">
                      {HACKATHON_PROPOSAL.outcomes.map((o, i) => (
                        <span key={i} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1" style={{ fontFamily: 'var(--font-body)' }}>{o}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Objections */}
      <div className="mb-10 stagger-4">
        <h3 className="font-bold text-slate-900 text-xl mb-5 text-center">Anticipated Questions</h3>
        <div className="space-y-2 max-w-3xl mx-auto">
          {OBJECTIONS.map((obj, i) => {
            const Icon = icons[obj.icon] || Globe;
            const isExp = expO === i;
            return (
              <div key={i} data-testid={`objection-card-${i}`}
                className={`objection-card rounded-xl border cursor-pointer ${isExp ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                onClick={() => setExpO(isExp ? null : i)}>
                <div className="p-4 flex items-center gap-3"><Icon className="w-5 h-5 text-slate-400 flex-shrink-0" /><span className="font-medium text-slate-900 text-sm flex-1" style={{ fontFamily: 'var(--font-body)' }}>"{obj.q}"</span>{isExp ? <ChevronUp className="w-4 h-4 text-blue-500" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}</div>
                {isExp && <div className="px-4 pb-4 tooltip-enter"><p className="text-sm text-slate-700 bg-blue-100/40 rounded-lg p-3" style={{ fontFamily: 'var(--font-body)' }}>{obj.a}</p></div>}
              </div>
            );
          })}
        </div>
      </div>
      {previewPdf && <PdfPreviewModal url={previewPdf.url} name={previewPdf.name} onClose={() => setPreviewPdf(null)} />}
      {/* CTA */}
      <div className="text-center max-w-2xl mx-auto stagger-6">
        <div className="bg-[#FFD500]/10 border-2 border-[#FFD500]/40 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-slate-900 mb-3">Suggested Next Step</h3>
          <p className="text-slate-700" style={{ fontFamily: 'var(--font-body)' }}>"Would you be open to a <span className="font-bold">15-minute call with Dr. Vilma Fuentes at FSU</span>? She's built three Ukraine MOUs and could validate the feasibility."</p>
        </div>
        <KobzarQuote quote={KOBZAR_QUOTES[1]} />
        <div className="mt-6 text-slate-500 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
          <p className="font-semibold text-slate-700" style={{ fontFamily: 'var(--font-display)' }}>Matthew R. MacFarlane</p>
          <p className="mt-1">MS Applied Data Science (Candidate) {'\u00B7'} New College of Florida</p>
          <p>matthew.macfarlane27@ncf.edu</p>
        </div>
      </div>
      <CitationBar citations={NEWS_CITATIONS.ask} />
    </SW>
  );
}
